import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { notify, notifyError } from "../../../utils/toast";
import Modal from "../../../components/common/modal";

const EVENT_CODE = "RESTAURANT_DISCOUNT_WAITLIST";

// 수원시 구 목록
const SUWON_DISTRICTS = [
  "수원시 영통구",
  "수원시 팔달구",
  "수원시 권선구",
  "수원시 장안구",
  "수원시 영덕구",
];

// 성남시 구 목록
const SEONGNAM_DISTRICTS = [
  "성남시 수정구",
  "성남시 분당구",
  "성남시 중원구",
];

const EVENT_WAIT_LIST_USERS_BY_REGION = gql`
  query eventWaitListUsersByRegion($eventCode: String!, $region: String!) {
    eventWaitListUsersByRegion(eventCode: $eventCode, region: $region)
  }
`;

const SEND_PUSH_TO_EVENT_WAIT_LIST = gql`
  mutation sendPushToEventWaitListByAdmin($input: Json!) {
    sendPushToEventWaitListByAdmin(input: $input) {
      ok
      error
    }
  }
`;

const AdminRestaurantDiscountWaitlistPush = () => {
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string>("전체");
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 선택된 지역 결정
  const selectedRegion =
    selectedDistrict === "전체"
      ? selectedCity === null
        ? "전체"
        : `${selectedCity} 전체`
      : selectedDistrict;

  const { data: countData, refetch: refetchCount } = useQuery(
    EVENT_WAIT_LIST_USERS_BY_REGION,
    {
      variables: {
        eventCode: EVENT_CODE,
        region: selectedRegion,
      },
      skip: false,
    }
  );

  const [sendPushNotification, { loading: isSending }] = useMutation(
    SEND_PUSH_TO_EVENT_WAIT_LIST,
    {
      onCompleted: (data) => {
        if (data.sendPushToEventWaitListByAdmin.ok) {
          notify("푸시 알림이 발송되었습니다.");
          setPushTitle("");
          setPushMessage("");
          refetchCount();
        } else {
          notifyError(
            data.sendPushToEventWaitListByAdmin.error ||
              "푸시 알림 발송 중 오류가 발생했습니다."
          );
        }
      },
      onError: (error) => {
        console.error("Error sending push notification:", error);
        notifyError("푸시 알림 발송 중 오류가 발생했습니다.");
      },
    }
  );

  const handleSendNotification = () => {
    if (!pushTitle || !pushMessage) {
      notifyError("제목, 메시지를 모두 입력해주세요.");
      return;
    }

    if (!selectedCity && selectedDistrict === "전체") {
      notifyError("시를 선택해주세요.");
      return;
    }

    // 확인 모달 표시
    setShowConfirmModal(true);
  };

  const handleConfirmSend = () => {
    setShowConfirmModal(false);
    sendPushNotification({
      variables: {
        input: {
          eventCode: EVENT_CODE,
          region: selectedRegion,
          title: pushTitle,
          message: pushMessage,
        },
      },
    });
  };

  const userCount = countData?.eventWaitListUsersByRegion ?? 0;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-8">
        음식점 할인 사전할인 푸시 알림
      </h1>

      {/* 이벤트 정보 */}
      <div className="mb-8 p-4 bg-blue-50 rounded-lg">
        <div className="text-md text-gray-700">
          <span className="font-bold">이벤트 코드:</span> {EVENT_CODE}
        </div>
        <div className="text-sm text-gray-500 mt-1">
          레스토랑 할인 오픈 사전알림 이벤트
        </div>
      </div>

      {/* 지역 선택 */}
      <div className="mb-8">
        <div className="block text-lg text-gray-700 mb-4 font-bold">
          📍 발송 지역 선택
        </div>

        {/* 시 선택 */}
        <div className="mb-4">
          <div className="text-sm text-gray-600 mb-2">시 선택</div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => {
                setSelectedCity(null);
                setSelectedDistrict("전체");
              }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                selectedCity === null
                  ? "bg-blue-500 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              전체
            </button>
            <button
              onClick={() => {
                setSelectedCity("수원시");
                setSelectedDistrict("전체");
              }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                selectedCity === "수원시"
                  ? "bg-blue-500 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              수원시
            </button>
            <button
              onClick={() => {
                setSelectedCity("성남시");
                setSelectedDistrict("전체");
              }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                selectedCity === "성남시"
                  ? "bg-blue-500 text-white shadow-md"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              성남시
            </button>
          </div>
        </div>

        {/* 구 선택 */}
        {selectedCity && (
          <div className="mb-4">
            <div className="text-sm text-gray-600 mb-2">구 선택</div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setSelectedDistrict("전체")}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                  selectedDistrict === "전체"
                    ? "bg-green-500 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                전체
              </button>
              {(selectedCity === "수원시"
                ? SUWON_DISTRICTS
                : SEONGNAM_DISTRICTS
              ).map((district) => (
                <button
                  key={district}
                  onClick={() => setSelectedDistrict(district)}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                    selectedDistrict === district
                      ? "bg-green-500 text-white shadow-md"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {district.replace(`${selectedCity} `, "")}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-md text-gray-700">
            선택한 지역:{" "}
            <span className="font-bold">
              {selectedRegion === "전체"
                ? selectedCity
                  ? `${selectedCity} 전체`
                  : "수원시/성남시 전체"
                : selectedRegion}
            </span>
            <br />
            신청자 수:{" "}
            <span className="font-bold">{userCount.toLocaleString()}</span>명
          </div>
        </div>
      </div>

      {/* 푸시 알림 제목 입력 */}
      <div className="mt-8">
        <div className="block text-lg text-gray-700 mb-2 font-bold">
          💬 푸시 알림 제목 입력 (필수)
        </div>
        <input
          type="text"
          placeholder="푸시 알림 제목을 입력하세요"
          value={pushTitle}
          onChange={(e) => setPushTitle(e.target.value)}
          className="border p-3 rounded w-full"
        />
      </div>

      {/* 푸시 알림 본문 입력 */}
      <div className="mt-4">
        <div className="block text-lg text-gray-700 mb-2 font-bold">
          💬 푸시 알림 본문 입력 (필수)
        </div>
        <textarea
          placeholder="푸시 알림 본문을 입력하세요"
          value={pushMessage}
          onChange={(e) => setPushMessage(e.target.value)}
          className="border p-3 rounded w-full h-32"
        />
      </div>

      {/* 발송 정보 요약 */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <div className="text-md text-gray-700">
          <span className="font-bold">{userCount.toLocaleString()}</span>명에게
          푸시 알림을 발송합니다.
        </div>
        <div className="text-sm text-gray-500 mt-1">
          * 제목과 본문을 모두 입력해주세요.
        </div>
      </div>

      {/* 발송 버튼 */}
      <button
        onClick={handleSendNotification}
        disabled={
          !pushTitle || !pushMessage || !selectedRegion || isSending || userCount === 0
        }
        className={`py-4 rounded-xl w-full mt-8 font-bold text-lg transition-all ${
          pushTitle && pushMessage && selectedRegion && !isSending && userCount > 0
            ? "bg-blue-500 text-white hover:bg-blue-600"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {isSending ? "발송 중..." : "푸시 알림 발송"}
      </button>

      {/* 확인 모달 */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onYes={handleConfirmSend}
        title={`${selectedRegion === "전체"
          ? selectedCity
            ? `${selectedCity} 전체`
            : "수원시/성남시 전체"
          : selectedRegion} ${userCount.toLocaleString()}명 맞으실까요?`}
        yesText="발송하기"
        noText="취소"
        yesButtonDisabled={isSending}
      />
    </div>
  );
};

export default AdminRestaurantDiscountWaitlistPush;
